import { useArchiveNoAudioDeadLetters, useGetSystemHealth, useRunCronTaskNow } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import {
  ClockIcon,
  ExclamationCircleIcon,
  InboxInIcon,
  LightningBoltIcon,
} from '@heroicons/react/outline';
import {
  ColumnHeader,
  EmptyState,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import {
  CronJobHealthDTO,
  CronTaskHealthDTO,
  StreamHealthDTO,
  StreamMessageSampleDTO,
} from '@shared/apis/systemHealth.apis';
import { MANUALLY_RUNNABLE_CRON_TASKS } from '@shared/constants';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { Fragment, ReactNode, useState } from 'react';

const REFRESH_MS = 60_000;

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top last:border-b-0';
const leftHead = 'px-5 py-3 text-left font-semibold';
const leftCell = 'px-5 py-3 text-left text-customLight';
const numberCell = 'px-5 py-3 text-right tabular-nums';
const pill = 'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold';

type Tone = 'ok' | 'warn' | 'bad' | 'muted';

const TONES: Record<Tone, string> = {
  ok: 'bg-emerald-500/15 text-emerald-300',
  warn: 'bg-amber-500/15 text-amber-300',
  bad: 'bg-red-500/15 text-red-300',
  muted: 'bg-white/10 text-customGrey',
};

const Pill = ({ tone, children }: { tone: Tone; children: ReactNode }) => (
  <span className={mergeClassNames(pill, TONES[tone])}>{children}</span>
);

const relative = (value?: string) => (value ? moment(value).fromNow() : '—');
const absolute = (value?: string) => (value ? moment(value).format('DD MMM YYYY, HH:mm') : '');

const formatDuration = (milliseconds: number) => {
  if (milliseconds < 1000) return `${milliseconds} ms`;
  const seconds = milliseconds / 1000;
  if (seconds < 120) return `${seconds.toFixed(1)} s`;
  return `${Math.round(seconds / 60)} min`;
};

const jobState = (job: CronJobHealthDTO): { tone: Tone; label: string } => {
  if (job.isStuck) return { tone: 'bad', label: 'Stuck lock' };
  if (job.tasks.some((task) => task.lastStatus === 'failed')) {
    return { tone: 'bad', label: 'Task failing' };
  }
  if (job.isOverdue) return { tone: 'warn', label: 'Overdue' };
  if (!job.lastRunAt) return { tone: 'muted', label: 'Never ran' };
  if (job.failReason) return { tone: 'warn', label: 'Last run failed' };
  return { tone: 'ok', label: 'OK' };
};

const streamState = (stream: StreamHealthDTO): { tone: Tone; label: string } => {
  const pending = stream.groups.reduce((sum, group) => sum + group.pending, 0);
  const realFailures = stream.deadLetterReasons
    .filter((item) => !item.isNoAudio)
    .reduce((sum, item) => sum + item.count, 0);
  if (realFailures) return { tone: 'bad', label: 'Dead letters' };
  if (stream.deadLetterCount) return { tone: 'warn', label: 'Only no-audio dead letters' };
  if (stream.length && !stream.groups.length) return { tone: 'warn', label: 'No consumer group' };
  if (stream.groups.length && stream.groups.every((group) => !group.consumers)) {
    return { tone: 'warn', label: 'No consumers' };
  }
  if (pending || stream.retryCount) return { tone: 'warn', label: 'Backlog' };
  return { tone: 'ok', label: 'OK' };
};

const ErrorDetails = ({ text }: { text?: string }) =>
  text ? (
    <details className="mt-1 max-w-[520px]">
      <summary className="cursor-pointer text-xs text-red-300">
        {text.split('\n')[0].slice(0, 140)}
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-black/30 p-3 text-xs text-customGrey">
        {text}
      </pre>
    </details>
  ) : null;

const REASON_LABELS: Record<string, string> = {
  no_output_files_found: 'No audio captured (recorder)',
  no_audio_captured: 'No audio captured (recorder)',
  no_chunks_generated: 'No speech in participant audio',
  audio_too_silent: 'Participant audio too quiet',
};

const reasonLabel = (reason?: string) =>
  reason ? REASON_LABELS[reason] ?? reason : 'No reason recorded';

const SampleList = ({
  title,
  samples,
  tone,
}: {
  title: string;
  samples: StreamMessageSampleDTO[];
  tone: 'red' | 'grey';
}) =>
  samples.length ? (
    <details className="mt-2">
      <summary
        className={mergeClassNames(
          'cursor-pointer text-xs',
          tone === 'red' ? 'text-red-300' : 'text-customGrey'
        )}
      >
        {title}
      </summary>
      <ul className="mt-2 flex flex-col gap-2">
        {samples.map((sample) => (
          <li key={sample.id} className="rounded-lg bg-black/30 p-2 text-xs">
            <div className="text-customGrey">{absolute(sample.at)}</div>
            <div className={tone === 'red' ? 'text-red-300' : 'text-customLight'}>
              {reasonLabel(sample.reason)}
            </div>
            <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap text-customGrey">
              {JSON.stringify(sample.fields, null, 2)}
            </pre>
          </li>
        ))}
      </ul>
    </details>
  ) : null;

const RunNowButton = ({ task }: { task: CronTaskHealthDTO }) => {
  const toast = useToast();
  const { mutate: runNow, isLoading } = useRunCronTaskNow({
    onSuccess: (result) => {
      if (result.status === 'success') {
        toast.success(`${result.task} finished in ${formatDuration(result.durationMs)}.`);
      } else {
        toast.error(`${result.task} failed. See the error in the table.`);
      }
    },
    onError: (error) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      toast.error(message ?? 'The task could not be started.');
    },
  });

  if (!MANUALLY_RUNNABLE_CRON_TASKS.includes(task.task)) return null;

  return (
    <button
      type="button"
      disabled={isLoading}
      className="ml-3 text-xs font-semibold text-customPrimary hover:underline disabled:opacity-50"
      onClick={() => runNow({ task: task.task })}
    >
      {isLoading ? 'Running…' : 'Run now'}
    </button>
  );
};

const StreamDetails = ({ stream }: { stream: StreamHealthDTO }) => {
  const toast = useToast();
  const [isConfirming, setIsConfirming] = useState(false);
  const noAudioCount = stream.deadLetterReasons
    .filter((item) => item.isNoAudio)
    .reduce((sum, item) => sum + item.count, 0);

  const { mutate: archive, isLoading } = useArchiveNoAudioDeadLetters({
    onSuccess: (result) => {
      toast.success(`Moved ${result.moved} no-audio entries to Skipped.`);
      setIsConfirming(false);
    },
    onError: (error) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      toast.error(message ?? 'The entries could not be moved.');
      setIsConfirming(false);
    },
  });

  return (
    <>
      {stream.deadLetterReasons.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {stream.deadLetterReasons.map((item) => (
            <span
              key={item.reason}
              className={mergeClassNames(
                'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                item.isNoAudio ? 'bg-white/10 text-customGrey' : 'bg-red-500/15 text-red-300'
              )}
            >
              {reasonLabel(item.reason)} · {item.count}
            </span>
          ))}
        </div>
      ) : null}
      {noAudioCount ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {isConfirming ? (
            <>
              <span className="text-customLight">
                Move {noAudioCount} no-audio entr{noAudioCount === 1 ? 'y' : 'ies'} out of the dead
                letters into Skipped?
              </span>
              <button
                type="button"
                disabled={isLoading}
                className="font-semibold text-customPrimary disabled:opacity-50"
                onClick={() => archive({ key: stream.key })}
              >
                {isLoading ? 'Moving…' : 'Move'}
              </button>
              <button
                type="button"
                disabled={isLoading}
                className="text-customGrey"
                onClick={() => setIsConfirming(false)}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="font-semibold text-customPrimary hover:underline"
              onClick={() => setIsConfirming(true)}
            >
              Move no-audio entries to Skipped
            </button>
          )}
        </div>
      ) : null}
      <SampleList title="Latest dead letters" samples={stream.deadLetterSamples} tone="red" />
      <SampleList title="Latest skipped (no audio)" samples={stream.skippedSamples} tone="grey" />
    </>
  );
};

const SystemHealth = () => {
  const { data, isLoading, isFetching, refetch } = useGetSystemHealth({
    refetchInterval: REFRESH_MS,
  });
  const [expandedJobs, setExpandedJobs] = useState<string[]>([]);

  if (isLoading || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const failingTasks = data.cronJobs.flatMap((job) =>
    job.tasks.filter((task) => task.lastStatus === 'failed')
  ).length;
  const unhealthyJobs = data.cronJobs.filter((job) => job.isOverdue || job.isStuck).length;
  const deadLetters = data.redis.streams.reduce(
    (sum, stream) =>
      sum +
      stream.deadLetterReasons
        .filter((item) => !item.isNoAudio)
        .reduce((total, item) => total + item.count, 0),
    0
  );
  const bullFailed = data.redis.bullQueues.reduce((sum, queue) => sum + queue.failed, 0);

  const toggleJob = (name: string) =>
    setExpandedJobs((previous) =>
      previous.includes(name) ? previous.filter((item) => item !== name) : [...previous, name]
    );

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">System Health</h1>
          <p className="max-w-3xl text-sm text-customGrey">
            Scheduled jobs of the API, Redis queues and failed Zoom webhook events for the{' '}
            <strong className="text-white">{data.environment}</strong> environment. Refreshes every
            minute; last read {relative(data.generatedAt)}.
          </p>
        </div>
        <Button
          variant="secondary"
          buttonText="Refresh"
          isLoading={isFetching}
          onClick={() => refetch()}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-4">
        <MetricCard
          label="Cron tasks failing on last run"
          value={failingTasks}
          icon={<ExclamationCircleIcon className="h-5 w-5" />}
          accent="gold"
        />
        <MetricCard
          label="Jobs overdue or stuck"
          value={unhealthyJobs}
          icon={<ClockIcon className="h-5 w-5" />}
          accent="lila"
        />
        <MetricCard
          label="Dead letters (real failures)"
          value={data.redis.available ? deadLetters : 'n/a'}
          icon={<InboxInIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="Failed webhook jobs (BullMQ)"
          value={data.redis.available ? bullFailed : 'n/a'}
          icon={<LightningBoltIcon className="h-5 w-5" />}
          accent="green"
        />
      </div>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Scheduled jobs"
          description="Each job runs several tasks in sequence; a failing task does not stop the others. Task history is kept for 90 days and only exists from this release on. Click a job to see its tasks."
        />
        {!data.cronJobs.length ? (
          <EmptyState>No scheduled jobs were found.</EmptyState>
        ) : (
          <TableShell minWidth="min-w-[960px]">
            <thead>
              <tr className={headRow}>
                <th className={leftHead}>Job</th>
                <th className={leftHead}>Schedule</th>
                <th className={leftHead}>Last run</th>
                <th className={leftHead}>Next run</th>
                <th className={leftHead}>State</th>
              </tr>
            </thead>
            <tbody>
              {data.cronJobs.map((job) => {
                const state = jobState(job);
                const isExpanded = expandedJobs.includes(job.name);

                return (
                  <Fragment key={job.name}>
                    <tr
                      className={mergeClassNames(bodyRow, 'cursor-pointer hover:bg-surfaceHover')}
                      tabIndex={0}
                      onClick={() => toggleJob(job.name)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') toggleJob(job.name);
                      }}
                    >
                      <td className={leftCell}>
                        <span className="mr-2 text-customGrey">{isExpanded ? '▾' : '▸'}</span>
                        {job.name}
                        <span className="ml-2 text-xs text-customGrey">
                          {job.tasks.length} task{job.tasks.length === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td className={mergeClassNames(leftCell, 'font-mono text-xs')}>
                        {job.schedule ?? '—'}
                      </td>
                      <td className={leftCell} title={absolute(job.lastRunAt)}>
                        {relative(job.lastRunAt)}
                      </td>
                      <td className={leftCell} title={absolute(job.nextRunAt)}>
                        {relative(job.nextRunAt)}
                      </td>
                      <td className="px-5 py-3">
                        <Pill tone={state.tone}>{state.label}</Pill>
                        {job.isStuck ? (
                          <div className="mt-1 text-xs text-customGrey">
                            Locked since {absolute(job.lockedAt)}
                          </div>
                        ) : null}
                        <ErrorDetails text={job.failReason} />
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr className={bodyRow}>
                        <td colSpan={5} className="bg-black/20 px-5 py-3">
                          {!job.tasks.length ? (
                            <span className="text-customGrey">
                              No task runs recorded yet. They appear after the next run.
                            </span>
                          ) : (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className={headRow}>
                                  <th className="py-2 text-left font-semibold">Task</th>
                                  <th className="py-2 text-left font-semibold">Last run</th>
                                  <th className="py-2 text-right font-semibold">Duration</th>
                                  <th className="py-2 text-right font-semibold">Runs 7d</th>
                                  <th className="py-2 text-right font-semibold">Failures 7d</th>
                                  <th className="py-2 pl-4 text-left font-semibold">Result</th>
                                </tr>
                              </thead>
                              <tbody>
                                {job.tasks.map((task) => (
                                  <tr key={task.task} className="border-b border-white/5 align-top">
                                    <td className="py-2 text-customLight">
                                      {task.task}
                                      <RunNowButton task={task} />
                                    </td>
                                    <td className="py-2" title={absolute(task.lastStartedAt)}>
                                      {relative(task.lastStartedAt)}
                                    </td>
                                    <td className="py-2 text-right tabular-nums">
                                      {formatDuration(task.lastDurationMs)}
                                    </td>
                                    <td className="py-2 text-right tabular-nums">{task.runs7d}</td>
                                    <td
                                      className={mergeClassNames(
                                        'py-2 text-right tabular-nums',
                                        task.failures7d ? 'text-red-300' : ''
                                      )}
                                    >
                                      {task.failures7d}
                                    </td>
                                    <td className="py-2 pl-4">
                                      <Pill tone={task.lastStatus === 'failed' ? 'bad' : 'ok'}>
                                        {task.lastStatus === 'failed' ? 'Failed' : 'Success'}
                                      </Pill>
                                      {task.lastStatus === 'failed' ? (
                                        <div className="mt-1 text-xs text-customGrey">
                                          Last success: {relative(task.lastSuccessAt)}
                                        </div>
                                      ) : null}
                                      <ErrorDetails text={task.lastError} />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </TableShell>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Redis streams"
          description="Pending = delivered to a worker but not acknowledged yet. Retry = waiting for another attempt. Dead letter = gave up, grouped by reason (red = real failures, grey = recordings without audio, which are expected). Skipped = no audio to process, acknowledged on purpose. Streams of every environment that shares this Redis are shown."
        />
        {!data.redis.available ? (
          <EmptyState>Redis could not be read: {data.redis.error}</EmptyState>
        ) : !data.redis.streams.length ? (
          <EmptyState>No streams were found.</EmptyState>
        ) : (
          <TableShell minWidth="min-w-[960px]">
            <thead>
              <tr className={headRow}>
                <th className={leftHead}>Stream</th>
                <th className={leftHead}>Env</th>
                <ColumnHeader>Length</ColumnHeader>
                <th className={leftHead}>Consumer groups</th>
                <ColumnHeader dot="gold">Retry</ColumnHeader>
                <ColumnHeader dot="lila">Dead letter</ColumnHeader>
                <ColumnHeader>Skipped (no audio)</ColumnHeader>
                <th className={leftHead}>State</th>
              </tr>
            </thead>
            <tbody>
              {data.redis.streams.map((stream) => {
                const state = streamState(stream);

                return (
                  <tr key={stream.key} className={bodyRow}>
                    <td className={leftCell}>
                      {stream.name}
                      <StreamDetails stream={stream} />
                    </td>
                    <td className={leftCell}>
                      <Pill tone={stream.environment === data.environment ? 'ok' : 'muted'}>
                        {stream.environment}
                      </Pill>
                    </td>
                    <td className={numberCell}>{stream.length}</td>
                    <td className={leftCell}>
                      {stream.groups.length
                        ? stream.groups.map((group) => (
                            <div key={group.name} className="text-xs">
                              <span className="text-customLight">{group.name}</span>
                              <span className="text-customGrey">
                                {' '}
                                · {group.consumers} consumer{group.consumers === 1 ? '' : 's'} ·{' '}
                                {group.pending} pending
                              </span>
                            </div>
                          ))
                        : '—'}
                    </td>
                    <td className={numberCell}>{stream.retryCount}</td>
                    <td
                      className={mergeClassNames(
                        numberCell,
                        stream.deadLetterCount ? 'text-red-300' : ''
                      )}
                    >
                      {stream.deadLetterCount}
                    </td>
                    <td className={numberCell}>{stream.skippedCount}</td>
                    <td className="px-5 py-3">
                      <Pill tone={state.tone}>{state.label}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableShell>
        )}
      </section>

      {data.redis.available ? (
        <section className="flex flex-col gap-4">
          <SectionHeading
            title="Zoom webhook queues (BullMQ)"
            description="Events from the webhook handler waiting for the webhook processor. The full job list is in Bull Board on the webhook handler."
          />
          <TableShell minWidth="min-w-[960px]">
            <thead>
              <tr className={headRow}>
                <th className={leftHead}>Queue</th>
                <ColumnHeader>Waiting</ColumnHeader>
                <ColumnHeader>Active</ColumnHeader>
                <ColumnHeader>Delayed</ColumnHeader>
                <ColumnHeader>Completed</ColumnHeader>
                <ColumnHeader dot="lila">Failed</ColumnHeader>
                <th className={leftHead}>Latest failures</th>
              </tr>
            </thead>
            <tbody>
              {data.redis.bullQueues.map((queue) => (
                <tr key={queue.name} className={bodyRow}>
                  <td className={leftCell}>{queue.name}</td>
                  <td className={numberCell}>{queue.waiting + queue.prioritized + queue.paused}</td>
                  <td className={numberCell}>{queue.active}</td>
                  <td className={numberCell}>{queue.delayed}</td>
                  <td className={numberCell}>{queue.completed}</td>
                  <td className={mergeClassNames(numberCell, queue.failed ? 'text-red-300' : '')}>
                    {queue.failed}
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {queue.recentFailures.length
                      ? queue.recentFailures.map((failure) => (
                          <div key={failure.id} className="mb-1">
                            <span className="text-customGrey">{absolute(failure.failedAt)} </span>
                            <span className="text-customLight">{failure.name ?? failure.id}</span>
                            {failure.reason ? (
                              <div className="text-red-300">{failure.reason}</div>
                            ) : null}
                          </div>
                        ))
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </section>
      ) : null}

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Failed Zoom webhook events (last 7 days)"
          description="Recordings, transcripts, chat or AI feedback steps that failed in the webhook processor."
        />
        {!data.failedEvents.length ? (
          <EmptyState>No failed events in the last 7 days.</EmptyState>
        ) : (
          <TableShell>
            <thead>
              <tr className={headRow}>
                <th className={leftHead}>Event</th>
                <th className={leftHead}>Env</th>
                <ColumnHeader dot="lila">Count</ColumnHeader>
                <th className={leftHead}>Last</th>
              </tr>
            </thead>
            <tbody>
              {data.failedEvents.map((event) => (
                <tr key={`${event.env}-${event.eventType}`} className={bodyRow}>
                  <td className={leftCell}>{event.eventType}</td>
                  <td className={leftCell}>{event.env}</td>
                  <td className={numberCell}>{event.count}</td>
                  <td className={leftCell} title={absolute(event.lastAt)}>
                    {relative(event.lastAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </section>
    </div>
  );
};

export default SystemHealth;
